// src/graphql/client.js
// GraphQL client utilities for making requests

import { minifyQuery } from './utils.js';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.bidlyzer.com';
const USE_COOKIES = import.meta.env.VITE_AUTH_USE_COOKIES === 'true';

/**
 * Execute a GraphQL request
 * @param {string} query - GraphQL query or mutation string
 * @param {object} variables - Variables to pass to the query
 * @param {string} token - Auth token (optional, for non-cookie auth)
 * @returns {Promise<object>} GraphQL response
 */
export const executeGraphQLRequest = async (query, variables = {}, token = null) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    if (!USE_COOKIES && token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(API_URL, {
        method: 'POST',
        headers,
        credentials: USE_COOKIES ? 'include' : 'same-origin',
        body: JSON.stringify({
            query: minifyQuery(query),
            variables,
        }),
    });

    if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
            throw new Error('AUTHENTICATION_EXPIRED');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
};

/**
 * Check if error is an authentication error
 * @param {object} result - GraphQL response
 * @returns {boolean}
 */
export const isAuthenticationError = (result) => {
    if (!result.errors) return false;

    return result.errors.some(err =>
        err.message.includes('unauthorized') ||
        err.message.includes('unauthenticated') ||
        err.extensions?.code === 'UNAUTHENTICATED'
    );
};

/**
 * Extract error messages from GraphQL response
 * @param {object} result - GraphQL response
 * @returns {string}
 */
export const extractErrorMessage = (result) => {
    if (!result.errors) return 'Unknown error';
    return result.errors.map(e => e.message).join(', ');
};
